import { Vec3, v3 } from 'cc';
import Game from '../../../../Game';
import globalInstance from '../../../../System/GlobalInstance';
import { easingEnumToFunction, EasingType } from '../../../Utils/EasingType';
import ControlPointPair from './ControlPointPair';
import CurveData from './CurveData';
import { PathDataConfigType, PathDataType, SEGEMENT_AVERAGE_DURATION, SegmentAverageDurationType, SpeedCurveDataType } from './PathDataType';
import RunPath from './RunPath';
import { SEG } from './SplineInterpolator';

export const SPEED_CURVE_LENGTH = 600;

export default class PathData {
    private _curveDatas: CurveData[];
    private _pairs: ControlPointPair[];
    private _name: string;
    private _runPath: RunPath = null;
    private _runSpeedCurve: RunPath = null;
    private _config: PathDataConfigType = null;
    private _isWorldPosition: boolean = true;
    private _isSpeedCurveEasing: boolean = false;
    private _isSegmentAverageDuration: boolean = false;
    private _gameInstance: Game = null;

    constructor(isWorldPosition: boolean = true) {
        this._name = 'deafaultPath';
        this._pairs = [];
        this._curveDatas = [];
        this._isWorldPosition = isWorldPosition;
    }

    public get runPath(): RunPath {
        if (!this._runPath) {
            this._runPath = this.computeRunPath(this._isWorldPosition);
        }
        return this._runPath;
    }

    public get Pairs() {
        return this._pairs;
    }
    public get name() {
        return this._name;
    }
    public set name(value: string) {
        this._name = value;
    }

    public get config() {
        return this._config;
    }

    public get arcLength(): number {
        let length: number = 0;
        this._curveDatas.forEach((data) => {
            length += data.arcLength;
        });
        return length;
    }

    public get totalDuration() {
        return this.config?.duration ?? this.curveDatas.map((curveData) => curveData.duration).reduce((acc, curr) => acc + curr, 0);
    }

    public get curveDatas(): CurveData[] {
        return this._curveDatas;
    }

    // 取得各線段的長度
    private get segmentLengths(): number[] {
        return this.curveDatas.map((curveData) => curveData.arcLength);
    }

    // 取得各線段實際花費時間的比例
    private get segmentDurationPercent(): number[] {
        const totalArcLength: number = this.segmentLengths.reduce((acc, curr) => acc + curr, 0);
        const percentArr = this.curveDatas.map((curveData) => curveData.arcLength / totalArcLength);
        return percentArr;
    }

    private get size(): number {
        return this._pairs.length;
    }

    public static import(data: PathDataType | SpeedCurveDataType, gameInstance = globalInstance.game): PathData {
        if (!data) {
            return null;
        }
        const isSpeedCurve = !(data as any).config;
        const path: PathData = new PathData(!isSpeedCurve);

        data.segments.forEach((segment) => {
            const { points } = segment;
            // 此處提供的座標是以中心座標為基準點的相對座標
            const pos1 = v3(points[1][0], points[1][1]); // MAIN POINT
            const pos2 = v3(points[0][0], points[0][1]); // SECOND 1
            const pos3 = v3(points[2][0], points[2][1]); // SECOND 2
            const pair = new ControlPointPair(pos1, pos2, pos3, segment.config);
            path.addPoint(pair);
        });
        path._name = data.name;
        // prevent speedCurve haven't config
        if (!isSpeedCurve) {
            path._config = (data as any).config;
            path.initSpeedCurveConfig(path._config.easing, gameInstance);
        }
        path.updateCurveData();
        path.updateInterpolationPointsAndArcLength(SEG);
        path._gameInstance = gameInstance;

        return path;
    }

    public initSpeedCurveConfig(easing: EasingType | string | SpeedCurveDataType | SegmentAverageDurationType, gameInstance = globalInstance.game) {
        this._config.easing = easing;
        if (Number.isNaN(this._config.easing) && typeof this._config.easing !== 'number') {
            if (this._config.easing === SEGEMENT_AVERAGE_DURATION) {
                this._isSegmentAverageDuration = true;
            } else if (typeof this._config.easing === 'string') {
                const speedCurve = gameInstance.bezierPath.getSpeed(this._config.easing);
                if (!speedCurve) {
                    this._config.easing = EasingType.linear;
                    return;
                }
                this._isSpeedCurveEasing = true;
                this._runSpeedCurve = speedCurve.runPath;
            } else {
                this._isSpeedCurveEasing = true;
                const existSpeedCurve = gameInstance.bezierPath.getSpeed(this._config.easing.name);
                if (existSpeedCurve) {
                    this._isSpeedCurveEasing = true;
                    this._runSpeedCurve = existSpeedCurve.runPath;
                    return;
                }
                const speedCurve = gameInstance.bezierPath.loadSingleSpeed(this._config.easing);
                this._runSpeedCurve = speedCurve.runPath;
            }
        }
    }

    public getSegmentAverageProgress(progress: number): number {
        const totalSegmentCurve = this._curveDatas.length;
        const splitPercent = 1 / totalSegmentCurve;
        const targetSegmentIndex = Math.floor(progress / splitPercent);
        if (targetSegmentIndex >= this._curveDatas.length) {
            return 1;
        }
        const targetSegmentProgress = (progress % splitPercent) / splitPercent;
        const actualLengthProgress =
            this._curveDatas.reduce((acc, curr, index) => {
                if (index < targetSegmentIndex) {
                    return acc + curr.arcLength;
                }
                return acc;
            }, 0) +
            this._curveDatas[targetSegmentIndex].arcLength * targetSegmentProgress;
        const targetProgress = actualLengthProgress / this.arcLength;
        return targetProgress;
    }

    public getEasingProgress(progress: number): number {
        if (this._isSpeedCurveEasing) {
            const targetX = progress * SPEED_CURVE_LENGTH;
            const targetFrame = this._runSpeedCurve.findIndexAtLeastX(targetX);
            if (targetFrame < 0) {
                return 1;
            }
            const targetProgress = this._runSpeedCurve.getPoint(targetFrame).y / SPEED_CURVE_LENGTH;
            return targetProgress;
        }
        if (this._isSegmentAverageDuration) {
            return this.getSegmentAverageProgress(progress);
        }
        // normal easing curve
        if (this._config.easing !== EasingType.linear) {
            return easingEnumToFunction(this._config.easing as number)(progress);
        }
        const { segmentDurationPercent } = this;
        let accPercent = 0;
        for (let i = 0; i < segmentDurationPercent.length; i++) {
            accPercent += segmentDurationPercent[i];
            if (progress < accPercent) {
                // 計算在該分段的進度
                const segmentProgress = (progress - (accPercent - segmentDurationPercent[i])) / segmentDurationPercent[i];
                // 在分段進度為0或1時，直接回傳進度，不需計算分段easing
                if (segmentProgress === 0) {
                    return accPercent - segmentDurationPercent[i];
                }
                if (segmentProgress === 1) {
                    return accPercent;
                }
                if (typeof this._pairs[i].config.easing === 'string') {
                    const speedCurve = this._gameInstance.bezierPath.getSpeed(`${this._pairs[i].config.easing}`).runPath;
                    const targetX = segmentProgress * SPEED_CURVE_LENGTH;
                    const targetFrame = speedCurve.findIndexAtLeastX(targetX);
                    if (targetFrame < 0) {
                        return 1;
                    }
                    const targetProgress = Math.min(1, speedCurve.getPoint(targetFrame).y / SPEED_CURVE_LENGTH);
                    return targetProgress * segmentDurationPercent[i] + (accPercent - segmentDurationPercent[i]);
                }
                return (
                    easingEnumToFunction(+this._pairs[i].config.easing)(segmentProgress) * segmentDurationPercent[i] + (accPercent - segmentDurationPercent[i])
                );
            }
        }
        return 0;
    }

    public computeRunPath(transformToWorldPosition: boolean = true): RunPath {
        const runPath: RunPath = new RunPath(transformToWorldPosition);
        // 取得整個曲線的控制點
        const segments: Vec3[] = this.getControlPoints();
        if (segments.length !== 0) {
            // 加入開頭位置
            runPath.controlPoints.push(v3(segments[1].x, segments[1].y));
            for (let j = 0; j < segments.length / 3 - 1; j++) {
                runPath.controlPoints.push(v3(segments[j * 3].x, segments[j * 3].y));
                runPath.controlPoints.push(v3(segments[(j + 1) * 3 + 2].x, segments[(j + 1) * 3 + 2].y));
                runPath.controlPoints.push(v3(segments[(j + 1) * 3 + 1].x, segments[(j + 1) * 3 + 1].y));
            }
        }
        // 取得所有線段的長度
        this._curveDatas.forEach((curveData) => {
            runPath.segmentLengths.push(curveData.arcLength);
        });
        runPath.duration = this.totalDuration;
        runPath.initRunTimeData(this.segmentDurationPercent);

        return runPath;
    }

    // 取得路徑所有的控制點(含路徑不經過的點)
    public getControlPoints(): Vec3[] {
        const controlPoints: Vec3[] = [];
        this._pairs.forEach((pair) => {
            pair.points.forEach((point) => {
                if (point) {
                    controlPoints.push(point);
                }
            });
        });
        return controlPoints;
    }

    public updateCurveData() {
        this._curveDatas = [];
        for (let i = 0; i < this.size - 1; i++) {
            const curve = new CurveData(this._pairs[i], this._pairs[i + 1]);
            this._curveDatas.push(curve);
        }
    }

    // 更新取樣點&弧長
    public updateInterpolationPointsAndArcLength(interpolatorSeg: number, changedPairIndex: number = -1) {
        if (changedPairIndex < 0 || changedPairIndex > this._curveDatas.length) {
            this._curveDatas.forEach((data) => {
                data.compute(interpolatorSeg);
            });
        } else if (changedPairIndex === 0) {
            if (this._curveDatas.length > 0) {
                this._curveDatas[0].compute(interpolatorSeg);
            }
        } else if (changedPairIndex === this.size - 1) {
            this._curveDatas[changedPairIndex - 1].compute(interpolatorSeg);
        } else {
            this._curveDatas[changedPairIndex].compute(interpolatorSeg);
            this._curveDatas[changedPairIndex - 1].compute(interpolatorSeg);
        }
    }

    // 給定主控制點位置，新增ControlPointPair到Path中
    public addPoint(pair: ControlPointPair, update: boolean = false) {
        this._pairs.push(pair);
        if (update) {
            this.updateCurveData();
            this.updateInterpolationPointsAndArcLength(SEG);
        }
    }
}
